/**
 * BroadcastHistoryTable - Tabela de histórico de broadcasts
 * Versão mobile-first com cards em telas pequenas
 */
import React from 'react'
import {
	Eye,
	RefreshCw,
	Trash2,
	Users,
	CheckCircle,
	XCircle,
	Mail,
	Send,
} from 'lucide-react'
import Card from '../../components/atoms/Card'
import Button from '../../components/atoms/Button'
import Badge from '../../components/atoms/Badge'
import { LoadingScreen } from '../../components/atoms/Spinner'
import { formatDate, formatNumber } from '../../core/admin.utils'
import { BROADCAST_TYPES, BROADCAST_STATUS } from '../core/broadcast.constants'
import { getBroadcastContent } from '../core/broadcast.utils'

const BroadcastHistoryTable = ({
	history,
	loading,
	onView,
	onResend,
	onDelete,
}) => {
	if (loading) {
		return (
			<Card className="p-4 md:p-6">
				<LoadingScreen />
			</Card>
		)
	}

	if (!history || history.length === 0) {
		return (
			<Card className="p-6 md:p-8 text-center">
				<Send className="w-12 h-12 mx-auto mb-4 text-text-muted opacity-50" />
				<p className="text-text-muted">Nenhum broadcast enviado</p>
			</Card>
		)
	}

	return (
		<div className="space-y-3 md:space-y-4">
			{history.map((broadcast) => {
				const typeInfo = BROADCAST_TYPES[broadcast.type] || BROADCAST_TYPES.push
				const statusInfo = BROADCAST_STATUS[broadcast.status] || BROADCAST_STATUS.draft
				const content = getBroadcastContent(broadcast)
				const displayTitle = content.pushTitle || content.emailSubject || 'Sem título'
				const displayMessage = content.pushBody || content.emailBody || ''
				const Icon = typeInfo.icon

				return (
					<Card key={broadcast._id} className="p-4">
						{/* Header - Tipo e Status */}
						<div className="flex items-start justify-between mb-3">
							<div className="flex items-center gap-2">
								<div className={`p-2 bg-${typeInfo.color}-500/20 rounded-lg`}>
									<Icon className={`w-4 h-4 text-${typeInfo.color}-500`} />
								</div>
								<div className="flex flex-wrap gap-1">
									<Badge variant={typeInfo.color} className="text-xs">
										{typeInfo.label}
									</Badge>
									<Badge variant={statusInfo.color} className="text-xs">
										{statusInfo.label}
									</Badge>
								</div>
							</div>
							<span className="text-xs text-text-muted whitespace-nowrap">
								{formatDate(broadcast.sentAt || broadcast.createdAt, 'dd/MM HH:mm')}
							</span>
						</div>

						{/* Conteúdo */}
						<div className="mb-3">
							<p className="font-medium text-text text-sm line-clamp-1">{displayTitle}</p>
							<p className="text-xs text-text-muted line-clamp-2 mt-1">
								{displayMessage?.substring(0, 100)}
							</p>
						</div>

						{/* Stats */}
						<div className="flex flex-wrap items-center gap-3 text-xs mb-3">
							<span className="text-text flex items-center gap-1">
								<Users className="w-3 h-3" />
								{formatNumber(broadcast.stats?.totalTargeted || 0)}
							</span>
							{broadcast.stats?.pushSent > 0 && (
								<span className="text-green-500 flex items-center gap-1">
									<CheckCircle className="w-3 h-3" />
									{formatNumber(broadcast.stats?.pushSent)} push
								</span>
							)}
							{broadcast.stats?.emailSent > 0 && (
								<span className="text-blue-500 flex items-center gap-1">
									<Mail className="w-3 h-3" />
									{formatNumber(broadcast.stats?.emailSent)} email
								</span>
							)}
							{(broadcast.stats?.pushFailed > 0 || broadcast.stats?.emailFailed > 0) && (
								<span className="text-red-500 flex items-center gap-1">
									<XCircle className="w-3 h-3" />
									{formatNumber((broadcast.stats?.pushFailed || 0) + (broadcast.stats?.emailFailed || 0))} falhas
								</span>
							)}
						</div>

						{/* Actions */}
						<div className="flex items-center gap-2 pt-2 border-t border-base-200">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onView(broadcast)}
								className="flex-1 text-xs"
							>
								<Eye className="w-3 h-3 mr-1" />
								Detalhes
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onResend(broadcast)}
								className="flex-1 text-xs"
							>
								<RefreshCw className="w-3 h-3 mr-1" />
								Reenviar
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => onDelete(broadcast)}
								className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
							>
								<Trash2 className="w-3 h-3" />
							</Button>
						</div>
					</Card>
				)
			})}
		</div>
	)
}

export default BroadcastHistoryTable
